/**
 * Identité de la société exploitante, servie par `GET /api/tenant`.
 *
 * Rien de tout cela ne peut être figé dans le bundle : une seule image
 * frontend sert toutes les sociétés (bsl, asap, ...), c'est le backend joint
 * qui dit de laquelle il s'agit.
 *
 * Le résultat est mis en cache pour toute la session : l'identité d'une
 * société ne change pas en cours d'utilisation.
 */
import { useEffect, useState } from "react";

/** Valeurs neutres tant que l'appel n'a pas répondu. */
const UNKNOWN = { name: "", shortName: "", emailDomain: "", hasLogo: false };

/** Servi par le backend depuis le répertoire `tenant/` de la société. */
export const TENANT_LOGO_URL = "/api/tenant/logo";

let cached = null;
let inFlight = null;

export function loadTenant() {
  if (cached) return Promise.resolve(cached);
  if (inFlight) return inFlight;

  inFlight = fetch("/api/tenant")
    .then((r) => (r.ok ? r.json() : UNKNOWN))
    .then((data) => {
      cached = { ...UNKNOWN, ...data };
      return cached;
    })
    .catch(() => UNKNOWN)
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * Identité du tenant. Renvoie des chaînes vides au premier rendu, puis la
 * valeur réelle — les appelants doivent traiter le cas « pas encore connu »
 * plutôt que d'afficher une valeur par défaut trompeuse.
 */
export function useTenant() {
  const [tenant, setTenant] = useState(cached || UNKNOWN);

  useEffect(() => {
    let alive = true;
    loadTenant().then((t) => {
      if (alive) setTenant(t);
    });
    return () => {
      alive = false;
    };
  }, []);

  return tenant;
}

/**
 * Nomme le document aux couleurs de la société servie : titre de l'onglet et
 * méta description.
 *
 * `public/index.html` porte un titre neutre (« Cotapilot »), puisque le bundle
 * est commun à toutes les sociétés. On le précise ici, une fois l'identité
 * connue. Le favicon, lui, n'a pas besoin d'être touché : index.html pointe
 * déjà sur `/api/tenant/logo`, servi par le backend de la société.
 *
 * À appeler une seule fois, au montage de l'application.
 */
export function applyTenantToDocument() {
  return loadTenant().then((tenant) => {
    if (!tenant.name) return tenant;

    document.title = `${tenant.name} | Livraison`;

    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.setAttribute("content", `${tenant.name} | Livraison`);
    }

    return tenant;
  });
}
